// ===== K-Tec AI Engine =====
// Main AI response generation engine
// Uses local rule-based logic when backend AI is unavailable
// Designed for real API integration via POST /api/ai/chat

import type { Language } from './languageDetector';
import type { ParsedRequirement } from './requirementParser';
import type { ConversationState } from './conversationMemory';
import type { SolutionRecommendation } from './productRecommendation';

// ── Response Templates ────────────────────────────────────────────────────────

export const WELCOME_MESSAGES = {
  sinhala: `👋 ආයුබෝවන්! මම K-Tec AI Assistant.

CCTV, Alarm Systems, Smart Home, Starlink සහ Networking ගැන ඔයාට උදව් කරන්න පුළුවන්.

සිංහලෙන්, English වලින් හෝ Singlish වලින් මගෙන් අහන්න.

**උදාහරණ:**
"මගේ ගෙදරට camera 4ක් ඕන"
"shop ekata cctv system ekak"`,

  english: `👋 Welcome! I'm the K-Tec AI Assistant.

I can help you with **CCTV Systems, Alarm Systems, Smart Home, Starlink & Networking**.

Ask me in Sinhala, English, or Singlish — I understand all three!

**Examples:**
"I need 4 cameras for my home"
"What CCTV package do you have for shops?"`,

  singlish: `👋 ආයුබෝවන්! I'm K-Tec AI Assistant.

CCTV, Alarm, Smart Home, Starlink, Networking — ovata help karanna puluwan.

Sinhala, English, Singlish — monawath kiyanna! 

**Example:**
"mage gedarata camera 4k oni"
"shop ekata system ekak"`,
};

// ── Question Templates ────────────────────────────────────────────────────────

export function getDiscoveryQuestions(req: ParsedRequirement, lang: Language, state: ConversationState): string {
  const asked = state.questionsAsked;

  if (lang === 'sinhala') {
    if (!req.propertyType && !asked.includes('property')) {
      return `හරි 👍 ඔයාගේ requirement එකට ගැළපෙන solution එකක් හදන්න මට පොඩි details ටිකක් ඕන.\n\nමෙය:\n• **ගෙදරක්**ද?\n• **Shop/Office**ද?\n• **Warehouse/Factory**ද?\n• **වෙනත් ස්ථානයක්**ද?`;
    }
    if (!req.cameraQuantity && !asked.includes('quantity')) {
      const prop = req.propertyType ? `ඔයාගේ ${req.propertyType} ` : '';
      return `${prop}camera කීයක් ඕනද? Outdoor areas, gate, parking ගැනත් කියන්න.\n\n*Rough idea ගියාලු හොඳයි, site survey එකෙන් confirm කරන්නම්.*`;
    }
    if (!req.fullColour && !req.nightVision && !asked.includes('night')) {
      return `**රෑට video quality** ගැන requirement එකක් තියෙනවද?\n\n• **Full Colour** (LED light නිසා රෑටත් colour video)\n• **IR Night Vision** (Black & White, long range)\n• **Standard** (Day vision only)`;
    }
    if (!req.budget && !asked.includes('budget')) {
      return `**Budget range** එකක් තියෙනවද? LKR ලෙස කියන්න.\n\nBudget දැනගත්තොත් ඔයාට best value solution එක recommend කරන්නත් easy.`;
    }
  } else if (lang === 'singlish') {
    if (!req.propertyType && !asked.includes('property')) {
      return `හරි 👍 oyage requirement ekata solution ekak hadanna mata podi details tikak oney.\n\nMeka:\n• **Gedarak**da?\n• **Shop / Office**da?\n• **Warehouse / Factory**da?\n• **Wenas thenaket**da?`;
    }
    if (!req.cameraQuantity && !asked.includes('quantity')) {
      return `Camera keeyak oney? Outdoor, gate, parking areas thibenawanam kiyanna.\n\n*Rough idea eka giyalu honda — site survey eken confirm karamu.*`;
    }
    if (!req.fullColour && !req.nightVision && !asked.includes('night')) {
      return `**Rathri video** requirement ekak thibenawadam?\n\n• **Full Colour** (Rathriyta colour video — LED light ekata)\n• **IR Night Vision** (Black & White, long range)\n• **Standard** (Day only)`;
    }
    if (!req.budget && !asked.includes('budget')) {
      return `**Budget ekak** thibenawadam? LKR amount eka kiyanna.\n\nBudget daenga oya best value solution eka pennannam easier.`;
    }
  } else {
    if (!req.propertyType && !asked.includes('property')) {
      return `Sure! 👍 To find the right solution, I need a few details.\n\nIs this for:\n• **Home / Residence**?\n• **Shop / Office**?\n• **Warehouse / Factory**?\n• **Other type of property**?`;
    }
    if (!req.cameraQuantity && !asked.includes('quantity')) {
      return `How many cameras do you need? Please also mention:\n• Outdoor areas (gate, parking)\n• Indoor areas (shop floor, cash counter, offices)\n\n*A rough estimate is fine — we'll confirm with a site survey.*`;
    }
    if (!req.fullColour && !req.nightVision && !asked.includes('night')) {
      return `What are your **night vision requirements**?\n\n• **Full Colour Night Vision** (colour video 24/7 with LED lighting)\n• **IR Night Vision** (black & white, long range, up to 60m)\n• **Standard** (day time only)`;
    }
    if (!req.budget && !asked.includes('budget')) {
      return `Do you have a **budget range** in mind? (in LKR)\n\nKnowing your budget helps me recommend the best value solution.`;
    }
  }

  return '';
}

// ── Solution Response ─────────────────────────────────────────────────────────

export function formatSolutionResponse(solution: SolutionRecommendation, lang: Language): string {
  const { cameras, recorder, storage, network, installationCost, labourCost, totalEstimate } = solution;

  if (lang === 'sinhala') {
    let msg = `🎯 **AI Recommended Solution**\n\n`;
    msg += `**${solution.title}**\n\n`;
    
    if (cameras.length > 0) {
      const cam = cameras[0];
      msg += `📷 **Cameras:** ${cam.quantity}× ${cam.product.name.substring(0, 50)}\n`;
    }
    if (recorder) {
      msg += `🖥️ **Recorder:** ${recorder.product.name.substring(0, 50)}\n`;
    }
    if (storage) {
      msg += `💾 **Storage:** ${storage.product.name.substring(0, 40)}\n`;
    }
    if (network) {
      msg += `🔌 **Network:** ${network.product.name.substring(0, 40)}\n`;
    }
    msg += `🔧 **Cat6 Cable + Accessories**\n`;
    msg += `👷 **Installation + Labour**\n\n`;
    
    msg += `**Estimated Total: LKR ${totalEstimate.toLocaleString()}**\n`;
    msg += `_(Installation: LKR ${installationCost.toLocaleString()} | Labour: LKR ${labourCost.toLocaleString()})_\n\n`;
    
    if (solution.storageEstimate) msg += `💡 ${solution.storageEstimate}\n\n`;
    
    msg += `✅ Phone eken live view\n`;
    msg += `✅ Recording + Playback\n`;
    msg += `✅ K-Tec Warranty\n\n`;
    msg += `_⚠️ AI estimate — final price site survey කරලා confirm කරමු._\n\n`;
    msg += `**Quotation හදා ගන්නද?**`;
    return msg;
  } else if (lang === 'singlish') {
    let msg = `🎯 **AI Recommended Solution**\n\n`;
    msg += `**${solution.title}**\n\n`;
    
    if (cameras.length > 0) {
      const cam = cameras[0];
      msg += `📷 **Cameras:** ${cam.quantity}× ${cam.product.name.substring(0, 50)}\n`;
    }
    if (recorder) msg += `🖥️ **Recorder:** ${recorder.product.name.substring(0, 50)}\n`;
    if (storage) msg += `💾 **Storage:** ${storage.product.name.substring(0, 40)}\n`;
    msg += `🔧 **Cable + Accessories**\n`;
    msg += `👷 **Installation**\n\n`;
    
    msg += `**Estimated Total: LKR ${totalEstimate.toLocaleString()}**\n\n`;
    msg += `✅ Phone eken live\n✅ Recording + Playback\n✅ K-Tec Warranty\n\n`;
    msg += `_⚠️ AI estimate — site survey eken final price confirm karamu._\n\n`;
    msg += `**Quotation ekak hadanna onadd?**`;
    return msg;
  } else {
    let msg = `🎯 **AI Recommended Solution**\n\n`;
    msg += `**${solution.title}**\n\n`;
    
    if (cameras.length > 0) {
      const cam = cameras[0];
      msg += `📷 **Cameras:** ${cam.quantity}× ${cam.product.name.substring(0, 50)}\n`;
    }
    if (recorder) msg += `🖥️ **Recorder:** ${recorder.product.name.substring(0, 50)}\n`;
    if (storage) msg += `💾 **Storage:** ${storage.product.name.substring(0, 40)}\n`;
    if (network) msg += `🔌 **Network:** ${network.product.name.substring(0, 40)}\n`;
    msg += `🔧 **Cat6 Cable + Accessories**\n`;
    msg += `👷 **Installation & Labour**\n\n`;
    
    msg += `**Estimated Total: LKR ${totalEstimate.toLocaleString()}**\n`;
    msg += `_(Installation: LKR ${installationCost.toLocaleString()} | Labour: LKR ${labourCost.toLocaleString()})_\n\n`;
    
    if (solution.storageEstimate) msg += `💡 ${solution.storageEstimate}\n\n`;
    
    msg += `✅ Remote viewing via mobile app\n`;
    msg += `✅ 24/7 Recording + Playback\n`;
    msg += `✅ K-Tec Installation Warranty\n\n`;
    msg += `_⚠️ This is an AI estimate. Final price will be confirmed after a site survey._\n\n`;
    msg += `**Would you like me to create a quotation?**`;
    return msg;
  }
}

// ── General Response Templates ────────────────────────────────────────────────

export function getQuotationRequestMessage(lang: Language): string {
  if (lang === 'sinhala') {
    return `Quotation request create කරන්න:\n\n**ඔයාගේ නමයි phone number එකයි දෙන්න** 📋\n\n_(Email හා address optional)_`;
  } else if (lang === 'singlish') {
    return `Quotation request create karanna:\n\n**Oyage name eka hari phone number eka kiyanna** 📋\n\n_(Email, address optional)_`;
  }
  return `To create your quotation request, please provide:\n\n**Your name and phone number** 📋\n\n_(Email and address are optional)_`;
}

export function getServiceInfoResponse(serviceType: string, lang: Language): string {
  const services: Record<string, { sinhala: string; english: string; singlish: string }> = {
    starlink: {
      sinhala: `🌐 **Starlink Internet — K-Tec Sri Lanka**\n\nK-Tec ලංකාවේ Official Starlink Reseller.\n\n**Starlink Plans:**\n• Residential — LKR ~20,000/month\n• Business — LKR ~40,000/month\n• Roam (portable) — LKR ~45,000/month\n\n**Hardware:** ~LKR 75,000 (one-time)\n\n**Features:**\n✅ High-speed satellite internet\n✅ 100-200 Mbps average\n✅ Low latency\n✅ Islandwide coverage\n✅ Work from anywhere\n\nK-Tec Starlink package ගැන විස්තර දැනගන්නද?`,
      english: `🌐 **Starlink Internet — K-Tec Sri Lanka**\n\nK-Tec is an Official Starlink Reseller in Sri Lanka.\n\n**Plans:**\n• Residential: ~LKR 20,000/month\n• Business: ~LKR 40,000/month\n• Roam (portable): ~LKR 45,000/month\n\n**Hardware:** ~LKR 75,000 (one-time cost)\n\n✅ High-speed satellite internet\n✅ 100-200 Mbps average speeds\n✅ Low latency\n✅ Islandwide coverage\n\nWould you like more information about Starlink packages?`,
      singlish: `🌐 **Starlink Internet — K-Tec Sri Lanka**\n\nK-Tec eka Official Starlink Reseller Sri Lanka wala.\n\n**Plans:**\n• Residential: ~LKR 20,000/month\n• Business: ~LKR 40,000/month\n\n**Hardware:** ~LKR 75,000 (one-time)\n\n✅ High speed satellite internet\n✅ 100-200 Mbps\n✅ Island wide coverage\n\nStarlink eka gena more details oney da?`,
    },
    alarm: {
      sinhala: `🔔 **Alarm Systems — K-Tec**\n\nK-Tec Smart Alarm Systems:\n\n• **PIR Motion Sensors** — Movement detection\n• **Magnetic Door Contacts** — Door/window opening detection\n• **GSM Alarm Panel** — SMS + call on trigger\n• **Siren + Strobe** — Audio/visual alert\n• **Smart Hub** — App control\n\n**Packages from LKR 25,000**\n\nHome, Shop, Office, Warehouse — ඕනෑම ස්ථානයකට design කරන්නෙමු.\n\nMore details ඕනද?`,
      english: `🔔 **Alarm Systems — K-Tec**\n\nK-Tec offers complete alarm solutions:\n\n• PIR Motion Sensors\n• Door/Window Contact Sensors\n• GSM Alarm Panel (SMS/Call alerts)\n• Siren & Strobe\n• Smart App Control\n\n**Starting from LKR 25,000**\n\nAvailable for homes, shops, offices, and warehouses.\n\nWould you like a custom alarm system designed for your property?`,
      singlish: `🔔 **Alarm Systems — K-Tec**\n\nK-Tec alarm packages:\n• PIR sensors, door contacts\n• GSM panel (SMS/call alerts)\n• App control\n\n**From LKR 25,000**\n\nOyage property ekata custom alarm system design karamu. Details kiyanna!`,
    },
  };

  const info = services[serviceType];
  if (!info) {
    if (lang === 'sinhala') return `K-Tec ${serviceType} services ගැන team members laga confirm karanna. Call us: 011-XXXX-XXXX`;
    return `For detailed information about K-Tec ${serviceType} services, please contact us or our team will assist you.`;
  }
  return info[lang] || info.english;
}

export function getNoProductsResponse(lang: Language): string {
  if (lang === 'sinhala') {
    return `😔 ඔයාගේ requirement match වෙන products catalog එකේ available නෑ.\n\n**ඇයි:**\n• Products catalog update නොවෙලා ඇති\n• Stock out ෙව්ලා ඇති\n\nK-Tec team ලාගෙන් confirm කරන්නද?\n📞 Hotline: [K-Tec Contact Number]`;
  } else if (lang === 'singlish') {
    return `😔 Oyage requirement match catalog eke nadda.\n\nK-Tec team laga confirm karanna.\n📞 Contact K-Tec directly.`;
  }
  return `😔 No matching products found in the current catalog.\n\nThis may be because:\n• Products not yet added to catalog\n• Items out of stock\n\nPlease contact K-Tec team directly for assistance.`;
}

export function getComparisonResponse(products: Array<{name: string; brand: string; price: number; specs: Record<string, string>}>, lang: Language): string {
  if (products.length < 2) {
    if (lang === 'sinhala') return 'Compare කරන්න product 2ක් select කරන්න ඕන.';
    return 'Please select at least 2 products to compare.';
  }
  // Simple text comparison
  let msg = '📊 **Product Comparison**\n\n';
  for (const p of products) {
    msg += `**${p.brand} — ${p.name}**\n`;
    for (const [key, val] of Object.entries(p.specs)) {
      msg += `• ${key}: ${val}\n`;
    }
    msg += `💰 Price: LKR ${p.price.toLocaleString()}\n\n`;
  }
  return msg;
}
